/**
 * Timed notes on the web (docs/timed-notes.md): what the composer posts, what a card says, and the
 * ring itself. A note the server has fired for this person rings once on this device: the peon says
 * his line, then the alarm chime loops until Stop, until anyone acknowledges it (a tap on the
 * notification, Stop on another phone), or RING_LOOP_MS (2 minutes, the server's ring window).
 * Stop is kept per device under `op.noteRung:<id>` for the moment before the server confirms the
 * acknowledgement, which then ends the ring for the whole household.
 *
 * Every label and rule comes from shared/notes.ts; this file only puts them together. Framework
 * free, so tests/timed-notes-web.test.ts runs it in Node.
 */
import { isRecipient, isRingingFor, noteAckLabel, noteExpiresInLabel, noteForLabel, noteRingsLabel, recipientsFor, RING_LOOP_MS, sameName, timerMs, type TimerParts } from "../shared/notes.ts";
import { isPushSound, PUSH_SOUND_LINES, RING_LINES } from "../shared/push-sounds.ts";
import { PUSH_ALERT_LINES, playVoiceFile, startAlarmLoop, stopAlarmLoop, type PlayingClip } from "./audio.ts";
import { isFeatureEnabled } from "./flags-core.ts";
import type { Note } from "../shared/types";

type TimedFields = Pick<Note, "id" | "fire_at" | "fired_at" | "recipients"> & Partial<Pick<Note, "acknowledged_at">>;

// ---------- The composer ----------

/** Who the composer has picked: everyone (the default), or names. */
export type Picked = "everyone" | string[];

/** No ring: the three wheels at zero, an ordinary note. */
export const NO_TIMER: TimerParts = { hours: 0, minutes: 0, seconds: 0 };

/** Each wheel's top value: hours 0 to 23, minutes and seconds 0 to 59, like the phone's timer. */
export const TIMER_PART_MAX: Readonly<Record<keyof TimerParts, number>> = { hours: 23, minutes: 59, seconds: 59 };

/**
 * One wheel turned: the parts with `unit` set from a select's value, a whole number clamped to the
 * wheel's range (anything that is not a number is 0).
 */
export function withTimerPart(parts: TimerParts, unit: keyof TimerParts, value: string | number): TimerParts {
  const n = Math.floor(Number(value));
  const v = Number.isFinite(n) ? Math.min(TIMER_PART_MAX[unit], Math.max(0, n)) : 0;
  return { ...parts, [unit]: v };
}

/**
 * The timer half of the POST body, or undefined for an ordinary note (the wheels at 0:0:0).
 * Recipients go through recipientsFor, so "everyone" and an empty pick are both null.
 */
export function timerRequest(parts: TimerParts, picked: Picked): { ring_in_ms: number; recipients: string[] | null } | undefined {
  const ms = timerMs(parts);
  if (!(ms > 0)) return undefined;
  return { ring_in_ms: ms, recipients: recipientsFor(picked) };
}

/**
 * The "Who" row's tap. Everyone resets the pick. The first name tapped away from Everyone keeps the
 * author in ("pie ready" is for the cook too) unless the tap was the author's own chip, which leaves
 * just the author. A tapped name already picked comes off; the last one off is Everyone again.
 */
export function togglePicked(current: Picked, tapped: string | "everyone", me: string): Picked {
  if (tapped === "everyone") return "everyone";
  if (current === "everyone") return sameName(tapped, me) ? [me] : [me, tapped];
  if (current.some((n) => sameName(n, tapped))) {
    const rest = current.filter((n) => !sameName(n, tapped));
    return rest.length ? rest : "everyone";
  }
  return [...current, tapped];
}

/** Whether a chip is shown picked. */
export function isPicked(current: Picked, name: string | "everyone"): boolean {
  if (name === "everyone") return current === "everyone";
  return current !== "everyone" && current.some((n) => sameName(n, name));
}

/** The roster for the "Who" row: the household's names with the author's own always among them. */
export function rosterWithMe(names: readonly string[], me: string): string[] {
  return names.some((n) => sameName(n, me)) ? [...names] : [me, ...names];
}

// ---------- The card ----------

/** Per-device mark that Stop was pressed for a note. */
export const RUNG_PREFIX = "op.noteRung:";
const rungHere = new Set<string>();

export function isRung(id: string): boolean {
  if (rungHere.has(id)) return true;
  try {
    return localStorage.getItem(RUNG_PREFIX + id) !== null;
  } catch {
    return false;
  }
}

export function markRung(id: string): void {
  rungHere.add(id);
  try {
    localStorage.setItem(RUNG_PREFIX + id, "1");
  } catch {
    /* kept for this page load in rungHere */
  }
}

/** Whether this card wears the ringing look now: fired for me, inside the ring window, nobody has got it, not stopped here. */
export function cardRinging(note: TimedFields, me: string, now: number, rung: (id: string) => boolean = isRung): boolean {
  return isRingingFor({ fire_at: note.fire_at, fired_at: note.fired_at, acknowledged_at: note.acknowledged_at ?? null, recipients: note.recipients ?? null }, me, now) && !rung(note.id);
}

/**
 * The card's muted line. An ordinary note: the expiry, as before. A timed note: the countdown in
 * the expiry's place and who it is for ("rings in 38 min · for Hue and Jeff"); while it rings,
 * "ringing · for everyone"; once it is over, who got it and the expiry come back after them
 * ("rang 5 min ago · Jeff got it · for everyone · expires in 23 h").
 *
 * The ring is over on this device as soon as anyone has acknowledged it, Stop was pressed here,
 * or the note is for other people: "rang just now" straight away, not "ringing", even inside the
 * ring window.
 */
export function noteMetaLine(
  note: Pick<Note, "id" | "expires_at" | "fire_at" | "fired_at" | "recipients"> & Partial<Pick<Note, "acknowledged_at" | "acknowledged_by">>,
  me: string,
  now: number,
  rung: (id: string) => boolean = isRung,
): string {
  const expiry = noteExpiresInLabel(note.expires_at, now);
  if (!note.fire_at) return expiry;
  const recipients = note.recipients ?? null;
  const forLine = noteForLabel(recipients, me);
  // Not yet fired by the server and not yet due by this clock: the countdown. A fired note never counts down.
  if (!note.fired_at && new Date(note.fire_at).getTime() > now) return `${noteRingsLabel(note.fire_at, now)} · ${forLine}`;
  const ack = noteAckLabel({ acknowledged_at: note.acknowledged_at ?? null, acknowledged_by: note.acknowledged_by ?? null }, me);
  const over = !!ack || rung(note.id) || !isRecipient(recipients, me);
  const rings = noteRingsLabel(note.fire_at, now, over, note.fired_at);
  if (rings === "ringing") return "ringing";
  // Rung: only who got it, or "rang" when nobody has (owner, 2026-10-04: "rang 1 h ago · you got it
  // · for everyone" was "wordy and not useful"). The head line still says when it was pinned and
  // when it expires.
  return ack || "rang";
}

/** The first note that should ring on this device now (the oldest fired first), or null. */
export function noteToRing<T extends TimedFields & { created_at?: string }>(notes: readonly T[], me: string, now: number, rung: (id: string) => boolean = isRung): T | null {
  const due = notes.filter((n) => cardRinging(n, me, now, rung));
  due.sort((a, b) => (a.fired_at ?? "").localeCompare(b.fired_at ?? ""));
  return due[0] ?? null;
}

// ---------- The ring ----------

/** The note id a push's tag names ("note-<id>"), or null. */
export function noteIdFromTag(tag: unknown): string | null {
  return typeof tag === "string" && tag.startsWith("note-") && tag.length > 5 ? tag.slice(5) : null;
}

/**
 * The line a push plays in the open app: its own line from the original voice when it names a known
 * sound and that voice is in use, or null for today's startled line. The ring's own sound
 * ("peon-ring") is the ring flow's business, so it is null here too.
 */
export function pushLineFor(sound: unknown, originalVoice: boolean): string | null {
  if (!originalVoice || !isPushSound(sound) || sound === "peon-ring") return null;
  return PUSH_SOUND_LINES[sound];
}

/** How long the ring waits for the peon's line before the chime starts anyway. */
const LINE_WAIT_MS = 6_000;
/** A push with no note id right after a Stop is that same ring arriving late: it does not ring again. */
const LATE_PUSH_MS = 60_000;

let ringing: { id: string | null; startedAt: number } | null = null;
/** Notes that have sounded in this page load: one ring each, even when it ended by itself. */
const sounded = new Set<string>();
let lastStopAt = 0;
export const RING_EVENT = "op:ring";

function emit(): void {
  try {
    window.dispatchEvent(new Event(RING_EVENT));
  } catch {
    /* no window (tests) */
  }
}

/** The note whose ring is sounding, "" for a ring started by a push that named no note, or null. */
export function ringingNow(): string | null {
  return ringing ? (ringing.id ?? "") : null;
}

function waitForLine(clip: PlayingClip | null): Promise<void> {
  if (!clip) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      clip.removeEventListener("ended", done);
      clip.removeEventListener("pause", done);
      resolve();
    };
    const timer = setTimeout(done, LINE_WAIT_MS);
    clip.addEventListener("ended", done);
    clip.addEventListener("pause", done);
  });
}

/**
 * Rings: the peon's line (RING_LINES[0] in the original voice, a startled line in the Warcraft one),
 * then the chime looped until stopRing or RING_LOOP_MS from the start. Only one ring at a time: a ring
 * already sounding adopts a note id it lacked and otherwise ignores the call. A note already stopped
 * on this device, or already sounded in this page, does not ring again.
 */
export function startRing(id: string | null, now = Date.now()): void {
  if (ringing) {
    if (ringing.id === null && id) {
      ringing.id = id;
      sounded.add(id);
      emit();
    }
    return;
  }
  if (id && (sounded.has(id) || isRung(id))) return;
  if (!id && now - lastStopAt < LATE_PUSH_MS) return;
  const ring = { id, startedAt: now };
  ringing = ring;
  if (id) sounded.add(id);
  emit();
  setTimeout(() => {
    if (ringing !== ring) return;
    stopAlarmLoop();
    // Ran its course: the server's ring window is over with it, so the card settles on its own
    // ("rang 2 min ago"); `sounded` keeps it from ringing again in this page. Not a Stop: nothing is
    // marked, and nobody "got it".
    ringing = null;
    emit();
  }, RING_LOOP_MS);
  void (async () => {
    const original = isFeatureEnabled("originalAssets");
    const line = original ? RING_LINES[0] : PUSH_ALERT_LINES[Math.floor(Math.random() * PUSH_ALERT_LINES.length)];
    const clip = await playVoiceFile(original ? "original" : "warcraft", line).catch(() => null);
    await waitForLine(clip);
    if (ringing !== ring) return;
    const left = RING_LOOP_MS - (Date.now() - ring.startedAt);
    if (left > 0) await startAlarmLoop(left).catch(() => false);
    // Stopped while the chime was loading: the loop must not outlive the ring.
    if (ringing !== ring) stopAlarmLoop();
  })();
}

/** Stop: marks the note stopped on this device (when it has an id) and ends the sound. */
export function stopRing(id: string | null): void {
  if (id) markRung(id);
  lastStopAt = Date.now();
  if (ringing && (id === null || ringing.id === null || ringing.id === id)) {
    ringing = null;
    stopAlarmLoop();
  }
  emit();
}

/**
 * Stop on the card: ends the ring here at once (stopRing) and acknowledges it for the whole
 * household (`POST /api/notes/:id/ack`, passed in as `post`), so every other phone stops too.
 * Resolves with the server's note ("you got it"), or null when the request failed: the local mark
 * still holds this device quiet.
 */
export async function stopAndAck<T>(id: string, post: (id: string) => Promise<T>): Promise<T | null> {
  stopRing(id);
  try {
    return await post(id);
  } catch {
    return null;
  }
}

/**
 * Someone else got it: on every notes refresh and tick, a ring sounding for a note that now carries
 * `acknowledged_at` ends at once (a tap on the notification, or Stop on another phone). Nothing is
 * marked: the acknowledgement itself keeps the card quiet. Returns whether it stopped one.
 */
export function stopIfAcknowledged(notes: readonly Pick<Note, "id" | "acknowledged_at">[]): boolean {
  const id = ringing?.id;
  if (!id || !notes.some((n) => n.id === id && n.acknowledged_at)) return false;
  ringing = null;
  stopAlarmLoop();
  emit();
  return true;
}

/** Clears the ring's memory (tests). */
export function resetRingForTests(): void {
  ringing = null;
  sounded.clear();
  rungHere.clear();
  lastStopAt = 0;
}
